// =============================================================================
// GuardedNavLink — a <NavLink> that asks before walking away from unsaved work.
//
// The bottom tab bar and the desktop rail are always on screen, including while
// someone is mid-capture. Before this, one thumb tap on "Home" threw away a
// half-typed Gate Intake with no question asked — the back arrow had a guard
// (BackButton) and these did not, which is the worst of both worlds: the
// operator learns the app protects them, then meets the one control that
// doesn't.
//
// Drop-in for NavLink: same props, same className/children render-prop API.
// The in-page sibling, for a plain <Link>, is GuardedLink.
//
// It intercepts ONLY when a page has armed the guard (src/lib/pageDirty.ts).
// On a clean page the click is not touched at all — NavLink navigates exactly
// as it did. Modified clicks (⌘/ctrl/shift/alt, middle button) are never
// intercepted either: those open a new tab and leave the typed page standing,
// so there is nothing to warn about. Nor is the tab for the page already on
// screen: tapping it does not leave (guardLeaveClick).
// =============================================================================

import { NavLink, useLocation, useNavigate, useResolvedPath, type NavLinkProps } from 'react-router-dom'
import { guardLeaveClick } from '@/lib/pageDirty'

export default function GuardedNavLink({ onClick, ...props }: NavLinkProps) {
  const navigate = useNavigate()
  const here = useLocation()
  const target = useResolvedPath(props.to, { relative: props.relative })
  return (
    <NavLink
      {...props}
      onClick={e => {
        onClick?.(e)
        guardLeaveClick(e, target.pathname + target.search, here.pathname + here.search, () => navigate(props.to))
      }}
    />
  )
}
